import { FC, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, User, Monitor, Settings, LogOut, LayoutDashboard, Calendar } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useAuth } from '../../../contexts/AuthContext';

export interface UserMenuProps {
  className?: string;
}

interface MenuLink {
  to: string;
  label: string;
  icon: React.ReactNode;
}

/**
 * UserMenu component - Dropdown menu for authenticated users
 * Shows user name/avatar with dropdown options based on role
 */
export const UserMenu: FC<UserMenuProps> = ({ className }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (!user) return null;

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    navigate('/');
  };

  // Get dashboard link based on user role
  const getDashboardLink = (): MenuLink => {
    switch (user.role) {
      case 'ADMIN':
      case 'SUPER_ADMIN':
        return {
          to: '/admin',
          label: 'Admin Dashboard',
          icon: <LayoutDashboard className="w-4 h-4" />,
        };
      case 'THERAPIST':
        return {
          to: '/therapist/dashboard',
          label: 'Therapist Dashboard',
          icon: <LayoutDashboard className="w-4 h-4" />,
        };
      default:
        return {
          to: '/dashboard',
          label: 'Dashboard',
          icon: <LayoutDashboard className="w-4 h-4" />,
        };
    }
  };

  const dashboardLink = getDashboardLink();

  // Get initials from user name
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div ref={menuRef} className={cn('relative', className)}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg',
          'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700',
          'transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Avatar */}
        <div
          className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center',
            'bg-primary-100 text-primary-700 font-medium text-sm'
          )}
        >
          {getInitials(user.name)}
        </div>
        <span className="hidden md:block text-sm font-medium max-w-[120px] truncate">
          {user.name}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-500 dark:text-slate-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute right-0 mt-2 w-56 origin-top-right',
            'bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-slate-900/50 ring-1 ring-black ring-opacity-5 dark:ring-slate-700',
            'py-1 z-50',
            'animate-in fade-in-0 zoom-in-95 duration-200'
          )}
          role="menu"
          aria-orientation="vertical"
        >
          {/* User info header */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
            <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
              {user.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{user.email}</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            {/* Dashboard */}
            <Link
              to={dashboardLink.to}
              onClick={() => setIsOpen(false)}
              className={cn(
                'flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300',
                'hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors'
              )}
              role="menuitem"
            >
              {dashboardLink.icon}
              {dashboardLink.label}
            </Link>

            {/* Booking History - for customers */}
            {user.role === 'CUSTOMER' && (
              <Link
                to="/dashboard?section=history"
                onClick={() => setIsOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300',
                  'hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors'
                )}
                role="menuitem"
              >
                <Calendar className="w-4 h-4" />
                Booking History
              </Link>
            )}

            {/* Active Devices - login session management for customers */}
            {user.role === 'CUSTOMER' && (
              <Link
                to="/sessions"
                onClick={() => setIsOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300',
                  'hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors'
                )}
                role="menuitem"
              >
                <Monitor className="w-4 h-4" />
                Active Devices
              </Link>
            )}

            {/* Profile */}
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className={cn(
                'flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300',
                'hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors'
              )}
              role="menuitem"
            >
              <User className="w-4 h-4" />
              Profile
            </Link>

            {/* Settings */}
            <Link
              to="/settings"
              onClick={() => setIsOpen(false)}
              className={cn(
                'flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-slate-300',
                'hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors'
              )}
              role="menuitem"
            >
              <Settings className="w-4 h-4" />
              Settings
            </Link>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 dark:border-slate-700 py-1">
            <button
              type="button"
              onClick={handleLogout}
              className={cn(
                'flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400',
                'hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors'
              )}
              role="menuitem"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

UserMenu.displayName = 'UserMenu';
